'use client';

import { ElementRef } from 'react';
import type { ComponentProps } from 'react';
import { cn } from '@/lib/utils';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';

export const SidebarInset = ({
  ref,
  className,
  ...props
}: ComponentProps<'main'> & { ref?: React.Ref<HTMLDivElement> }) => {
  return (
    <main
      ref={ref}
      className={cn(
        'bg-background relative flex w-full flex-1 flex-col',
        'md:peer-data-[variant=inset]:m-2 md:peer-data-[variant=inset]:ml-0 md:peer-data-[variant=inset]:rounded-xl md:peer-data-[variant=inset]:shadow-sm md:peer-data-[variant=inset]:peer-data-[state=collapsed]:ml-2',
        className
      )}
      {...props}
    />
  );
};
SidebarInset.displayName = 'SidebarInset';

export const SidebarInput = ({
  ref,
  className,
  ...props
}: ComponentProps<typeof Input> & {
  ref?: React.RefObject<ElementRef<typeof Input> | null>;
}) => {
  return (
    <Input
      ref={ref}
      data-sidebar="input"
      className={cn(
        'bg-background focus-visible:ring-sidebar-ring h-8 w-full shadow-none focus-visible:ring-2',
        className
      )}
      {...props}
    />
  );
};
SidebarInput.displayName = 'SidebarInput';

export const SidebarHeader = ({
  ref,
  className,
  ...props
}: ComponentProps<'div'> & { ref?: React.Ref<HTMLDivElement> }) => {
  return (
    <div
      ref={ref}
      data-sidebar="header"
      className={cn('flex flex-col gap-2 p-2', className)}
      {...props}
    />
  );
};
SidebarHeader.displayName = 'SidebarHeader';

export const SidebarFooter = ({
  ref,
  className,
  ...props
}: ComponentProps<'div'> & { ref?: React.Ref<HTMLDivElement> }) => {
  return (
    <div
      ref={ref}
      data-sidebar="footer"
      className={cn('flex flex-col gap-2 p-2', className)}
      {...props}
    />
  );
};
SidebarFooter.displayName = 'SidebarFooter';

export const SidebarSeparator = ({
  ref,
  className,
  ...props
}: ComponentProps<typeof Separator> & {
  ref?: React.RefObject<ElementRef<typeof Separator> | null>;
}) => {
  return (
    <Separator
      ref={ref}
      data-sidebar="separator"
      className={cn('bg-sidebar-border mx-2 w-auto', className)}
      {...props}
    />
  );
};
SidebarSeparator.displayName = 'SidebarSeparator';

export const SidebarContent = ({
  ref,
  className,
  ...props
}: ComponentProps<'div'> & { ref?: React.Ref<HTMLDivElement> }) => {
  return (
    <div
      ref={ref}
      data-sidebar="content"
      className={cn(
        'flex min-h-0 flex-1 flex-col gap-2 overflow-auto group-data-[collapsible=icon]:overflow-hidden',
        className
      )}
      {...props}
    />
  );
};
SidebarContent.displayName = 'SidebarContent';
